import { useEffect, useState, useMemo, useCallback, useRef } from "react";

const isDatabaseDeletedError = (error) => {
  const errorMessage = error?.message || error;
  return (
    errorMessage &&
    typeof errorMessage === "string" &&
    (errorMessage.includes("Database deleted by request") ||
      errorMessage.includes("deleted by request of the user"))
  );
};

const isDatabaseConnectionLost = (error) => {
  const errorMessage = error?.message || error;
  const errorName = error?.name;

  const messageCheck =
    errorMessage &&
    typeof errorMessage === "string" &&
    (errorMessage.includes("Database connection failed.") ||
      errorMessage.includes("The database connection is closing"));

  // AbortError → Aborted transaction, which can occur due to a connection closure.
  // InvalidStateError → An invalid operation, for example, on a closed connection.
  // UnknownError → May indicate an unexpected connection loss.
  const nameCheck =
    errorName &&
    (errorName === "AbortError" ||
      errorName === "InvalidStateError" ||
      errorName === "UnknownError");

  return messageCheck || nameCheck;
};

const isSecurityContextError = (error) => {
  const errorMessage = error?.message || error;
  const errorName = error?.name;

  const messageCheck =
    errorMessage &&
    typeof errorMessage === "string" &&
    errorMessage.includes(
      "IDBFactory.open() called in an invalid security context"
    );
  const nameCheck = errorName === "SecurityError";

  return messageCheck || nameCheck;
};

// Singleton instance for IndexedDB connection
let dbInstance = null;
// Global flag for fallback mode (only work with react state, iframe and unable indexedDB access friendly)
let fallbackMode = false;

const getDBInstance = (dbName, storeName) => {
  if (fallbackMode) {
    return Promise.reject(new Error("Fallback mode: IndexedDB unavailable"));
  }

  if (dbInstance) {
    return dbInstance;
  }

  const connectWithRetry = (delay = 1000) => {
    return new Promise((resolve, reject) => {
      const request = window.indexedDB.open(dbName);

      const retry = () => {
        console.log("Retrying IndexedDB connection...");
        setTimeout(() => {
          connectWithRetry(delay * 2)
            .then(resolve)
            .catch(reject);
        }, delay);
      };

      let timeout = setTimeout(() => {
        request.onerror = null;
        request.onsuccess = null;
        console.log("Connection timeout.");
        retry();
      }, 5000);

      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        if (db && !db.objectStoreNames.contains(storeName)) {
          db.createObjectStore(storeName);
        }
      };

      request.onsuccess = (event) => {
        clearTimeout(timeout);
        const db = event.target.result;

        // Add db.onerror handler for runtime database errors
        db.onerror = (event) => {
          console.error("Database operation error:", event.target.error);
          dbInstance = null;
          connectWithRetry().then(resolve).catch(reject);
        };

        // Handle database closure
        db.onclose = () => {
          console.warn(
            "Database connection closed unexpectedly. Attempting to reconnect..."
          );
          dbInstance = null;
          connectWithRetry().then(resolve).catch(reject);
        };

        // Handle version changes
        db.onversionchange = () => {
          db.close();
          console.warn(
            "Database version changed, closing connection and attempting to reconnect..."
          );
          dbInstance = null;
          connectWithRetry().then(resolve).catch(reject);
        };

        resolve(db);
      };

      request.onerror = (event) => {
        clearTimeout(timeout);
        const error = event.target.error;
        console.error("Database error:", error);
        if (isSecurityContextError(error)) {
          console.warn(
            "IndexedDB unavailable due to security context. Enabling fallback mode."
          );
          fallbackMode = true;
          reject(error);
        } else {
          retry();
        }
      };
    });
  };

  dbInstance = connectWithRetry();
  return dbInstance;
};

export const useIndexedDB = (
  dbName,
  storeName,
  key,
  initialValue,
  setError = () => {}
) => {
  const [storedValue, setStoredValue] = useState(initialValue);
  const [loading, setLoading] = useState(true);
  const lastSaveRef = useRef(Promise.resolve());

  const handleErrors = useCallback(
    async ({ error, retryFn, delay }) => {
      const increasedDelay = delay * 2;
      if (isSecurityContextError(error)) {
        console.warn("IndexedDB security error, switching to fallback mode.");
        fallbackMode = true;
        setStoredValue(initialValue);
        setLoading(false);
        return initialValue;
      }
      const runRetry = async () => {
        setError(error.message);
        await new Promise((resolve) =>
          setTimeout(() => resolve(), increasedDelay)
        );
        return retryFn(increasedDelay);
      };
      if (isDatabaseConnectionLost(error)) {
        console.log("Database connection lost, retrying...");
        dbInstance = null;
        return await runRetry();
      } else if (isDatabaseDeletedError(error)) {
        console.log("Database deleted, retrying...");
        dbInstance = null;
        setStoredValue(initialValue);
        return await runRetry();
      }
      throw error;
    },
    [initialValue, setError]
  );

  const saveToDB = useCallback(
    (valueToStore) => {
      if (fallbackMode) {
        console.log("Fallback mode: Skipping save to DB.");
        return Promise.resolve();
      }

      const saveWithRetry = async (value, delay = 1000) => {
        try {
          const db = await getDBInstance(dbName, storeName);
          if (!db) {
            throw new Error("Database connection failed.");
          }
          // Check if the store exists
          if (!db.objectStoreNames.contains(storeName)) {
            const currentVersion = db.version;
            db.close();
            // Open with incremented version to create the store
            const upgradeRequest = window.indexedDB.open(
              dbName,
              currentVersion + 1
            );
            upgradeRequest.onupgradeneeded = (event) => {
              const upgradeDb = event.target.result;
              if (
                upgradeDb &&
                !upgradeDb.objectStoreNames.contains(storeName)
              ) {
                upgradeDb.createObjectStore(storeName);
              }
            };
            await new Promise((resolve, reject) => {
              upgradeRequest.onsuccess = (event) => {
                const newDb = event.target.result;
                dbInstance = Promise.resolve(newDb); // Update the singleton
                resolve(newDb);
              };
              upgradeRequest.onerror = (event) => {
                reject(event.target.error);
              };
            });
          }
          // Now the store exists, proceed with operations
          const transaction = db.transaction([storeName], "readwrite");
          const store = transaction.objectStore(storeName);
          store.put(valueToStore, key);
          await new Promise((resolve, reject) => {
            transaction.oncomplete = resolve;
            transaction.onerror = () => reject(transaction.error);
          });
        } catch (error) {
          await handleErrors({
            error,
            retryFn: (delay) => saveWithRetry(value, delay),
            delay,
          });
        }
      };

      return saveWithRetry(valueToStore).catch((error) => {
        const errorMessage = error?.message || error;
        console.error("Error saving to DB:", errorMessage);
        setError(errorMessage);
        throw error; // critical unexpected error
      });
    },
    [dbName, storeName, key, handleErrors, setError]
  );

  const getValue = useCallback(() => {
    if (fallbackMode) {
      console.log("Fallback mode: Using initial value only.");
      setStoredValue(initialValue);
      setLoading(false);
      return;
    }

    const getWithRetry = async (delay = 1000) => {
      try {
        setLoading(true);
        const db = await getDBInstance(dbName, storeName);
        if (!db) {
          throw new Error("Database connection failed.");
        }
        // Check if the store exists
        if (!db.objectStoreNames.contains(storeName)) {
          const currentVersion = db.version;
          db.close();
          // Open with incremented version to create the store
          const upgradeRequest = window.indexedDB.open(
            dbName,
            currentVersion + 1
          );
          upgradeRequest.onupgradeneeded = (event) => {
            const upgradeDb = event.target.result;
            if (upgradeDb && !upgradeDb.objectStoreNames.contains(storeName)) {
              upgradeDb.createObjectStore(storeName);
            }
          };
          await new Promise((resolve, reject) => {
            upgradeRequest.onsuccess = (event) => {
              const newDb = event.target.result;
              dbInstance = Promise.resolve(newDb); // Update the singleton
              resolve(newDb);
            };
            upgradeRequest.onerror = (event) => {
              reject(event.target.error);
            };
          });
        }
        // Now the store exists, proceed with operations
        const transaction = db.transaction([storeName], "readonly");
        const store = transaction.objectStore(storeName);
        const getRequest = store.get(key);
        const result = await new Promise((resolve, reject) => {
          getRequest.onsuccess = () => resolve(getRequest.result);
          getRequest.onerror = (event) => reject(event.target.error);
        });
        if (result !== undefined) {
          setStoredValue(result);
        } else {
          setStoredValue(initialValue);
          lastSaveRef.current = lastSaveRef.current.then(() =>
            saveToDB(initialValue)
          );
        }
      } catch (error) {
        await handleErrors({
          error,
          retryFn: (delay) => getWithRetry(delay),
          delay,
        });
      } finally {
        setLoading(false);
      }
    };

    getWithRetry().catch((error) => {
      const errorMessage = error?.message || error;
      console.error("Error getting value:", errorMessage);
      setError(errorMessage);
      throw error; // critical unexpected error
    });
  }, [dbName, storeName, key, initialValue, saveToDB, handleErrors, setError]);

  useEffect(() => {
    getValue();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setValue = useCallback(
    (newValue) => {
      return new Promise((resolve, reject) => {
        setStoredValue((prevState) => {
          const valueToStore =
            typeof newValue === "function" ? newValue(prevState) : newValue;
          if (!fallbackMode) {
            const savePromise = lastSaveRef.current.then(() =>
              saveToDB(valueToStore)
            );
            lastSaveRef.current = savePromise.catch((e) => {
              console.error("Save chain error:", e);
            });
            savePromise.then(resolve).catch(reject);
          } else {
            resolve();
          }
          return valueToStore;
        });
      });
    },
    [saveToDB]
  );

  const generateStatePropertyFunctions = useCallback(
    (property) => {
      return {
        [`set${property[0].toUpperCase()}${property.slice(1)}`]: (value) => {
          return setValue((prevState) => {
            const valueToSet =
              typeof value === "function" ? value(prevState[property]) : value;
            return { ...prevState, [property]: valueToSet };
          });
        },
      };
    },
    [setValue]
  );

  const statePropertyFunctions = useMemo(() => {
    return Object.keys(storedValue || {}).reduce((acc, property) => {
      return { ...acc, ...generateStatePropertyFunctions(property) };
    }, {});
  }, [storedValue, generateStatePropertyFunctions]);

  return [storedValue, setValue, statePropertyFunctions, loading];
};
